import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ImpersonateButton from '../shared/ImpersonateButton';

// Same-site paths only: this becomes a redirect target once the masquerade starts.
const sameSite = (path: string | null) => (
  path != null && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') ? path : null
);

const storageKey = (stafferId: string | number) => `gradeSync.returnTo.${stafferId}`;

const remembered = (stafferId: string | number) => {
  try {
    return sameSite(window.sessionStorage.getItem(storageKey(stafferId)));
  } catch {
    return null;
  }
};

interface ReturnToTeacherProps {
  name: string | null;
  stafferId: string | number;
}

// AIDEV-NOTE: Support opens the dashboard from inside a teacher's account (Open in Grade Sync ends the
// masquerade on the way, passing the teacher-side page as ?return_to). After a fix they want to check
// it as the teacher, so this starts the masquerade again and lands on that page. The page is kept in
// sessionStorage per teacher so it survives moving between the teacher and assignment pages.
const ReturnToTeacher = ({ name, stafferId }: ReturnToTeacherProps) => {
  const [params] = useSearchParams();
  const fromUrl = sameSite(params.get('return_to'));

  useEffect(() => {
    if (fromUrl == null) return;

    try {
      window.sessionStorage.setItem(storageKey(stafferId), fromUrl);
    } catch {
      // Storage can be unavailable (private windows); the button falls back to the teacher's home page.
    }
  }, [fromUrl, stafferId]);

  const toPath = fromUrl ?? remembered(stafferId);
  const label = name ?? 'the teacher';

  return (
    <ImpersonateButton
      id={stafferId}
      label={toPath == null ? `Impersonate ${label}` : `Return to ${label}'s account`}
      name={name}
      toPath={toPath}
      userType="staffer"
    />
  );
};

export default ReturnToTeacher;
